import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

// Project import
import useAuth from "./useAuth";
import { simulateSpecies as simulateSpeciesLib } from "lib/simulation/simulation";
import { getColorComponentById } from "components/select/lineUtility";
import { FeatureStyleObject } from "types/firestoreFeatures";
import { PointOptionsProgramDefined, SimulateSpeciesProps } from "types/stand";
import { EstimationModels } from "types/simulation";
import { sortAccordingToOrder } from "utils/sorting";
import { InventoryIdGroup } from "types/auth";
import { useTranslation } from "utils/locales/utilityFunctions";
import { INVENTORY_WOOD_LOG_SPECIES } from "config/config-general";
import { getCustomLineColorId } from "utils/lineStyle";
import { getCustomPolygonColorId } from "utils/polygonStyle";

// Default values
const DEFAULT_SPC_COLOR = "#F2F2F2";
const DEFUALT_FOLIAGE_SPC_MODEL_TYPE = "Ahorn - ÆR";
const DEFUALT_NEEDLE_SPC_MODEL_TYPE = "Nobilis - NOB";
const LINE_CUSTOM_ORDER = [
  "Road",
  "Forest Road",
  "Track",
  "Trail",
  "Stone and earth dikes",
  "WaterStream",
  "Ditch",
];
const NATURE_CUSTOM_ORDER = [
  "Meadow",
  "Protected area",
  "Heath",
  "Bog",
  "Pasture",
  "Coastal meadow",
  "Lake",
];
const POINT_CUSTOM_ORDER = ["WoodStack", "WoodLogStack", "WoodChipStack"];

const useSpeciesData = () => {
  // --- Hooks --- //
  const { species, speciesSchemes, selectedSpeciesSchemeId } = useAuth();
  const { locale } = useIntl();
  const { translate } = useTranslation();

  // --- References and state --- //
  const langRef = useRef(locale);
  const speciesListRef = useRef(species);
  const [schemeChanged, setSchemeChanged] = useState(false);

  // --- Effects --- //
  useEffect(() => {
    let updatedSpecies = { ...species };
    if (speciesSchemes && selectedSpeciesSchemeId) {
      if (
        speciesSchemes[selectedSpeciesSchemeId] &&
        speciesSchemes[selectedSpeciesSchemeId].overwrites
      ) {
        Object.entries(speciesSchemes[selectedSpeciesSchemeId].overwrites).forEach((spc) => {
          const [key, value] = spc;
          if (updatedSpecies[key]) {
            updatedSpecies[key] = { ...updatedSpecies[key], ...value };
          }
        });
      }
      setSchemeChanged(!schemeChanged);
    }
    speciesListRef.current = updatedSpecies;
  }, [species, speciesSchemes, selectedSpeciesSchemeId]);

  useEffect(() => {
    langRef.current = locale;
  }, [locale]);

  // --- Function --- //
  const getSpeciesAvailability = (species: string) => {
    let availability = "simple";
    if (species === "") return availability;
    try {
      availability = speciesListRef.current![species].availability;
    } catch (error) {
      console.warn("Species not recognised", { species, error });
    }
    return availability;
  };

  const getSpeciesColor = (species: string, styleObject?: FeatureStyleObject): string => {
    let color = DEFAULT_SPC_COLOR;
    if (species === "") return getColorComponentById(getCustomPolygonColorId(styleObject));
    try {
      color = speciesListRef.current![species].color
        ? (speciesListRef.current![species].color as string)
        : DEFAULT_SPC_COLOR;
    } catch (error) {
      console.warn("Species not recognised", { species, error });
    }
    return color;
  };

  const getSpeciesModelType = (species: string) => {
    let modelType = DEFUALT_NEEDLE_SPC_MODEL_TYPE;
    if (getSpeciesType(species) === "foliage") modelType = DEFUALT_FOLIAGE_SPC_MODEL_TYPE;
    try {
      modelType = speciesListRef.current![species].estModel
        ? (speciesListRef.current![species].estModel as string)
        : modelType;
    } catch (error) {
      console.warn("Model type not recognised", { error, species });
    }
    return modelType as EstimationModels;
  };

  const getSpeciesType = (species: string) => {
    let type = "other";
    try {
      type = speciesListRef.current![species].type;
    } catch (error) {
      console.warn("Type not recognised", { error, species });
    }
    return type;
  };

  const getSpeciesNameAndAbbreviation = (
    species: string,
    lang?: string
  ): { name: string; abbreviation: string } => {
    let out = { name: "", abbreviation: "" };
    if (!species || species === "") return out;
    try {
      let language = lang || langRef.current;
      const found = speciesListRef.current![species].translations.filter(
        (el) => el.language.toLowerCase() === language
      );
      // Check for chosen language
      if (found.length > 0) {
        return found[0].species;
      }
      // Check for english
      const defaultFound = speciesListRef.current![species].translations.filter(
        (el) => el.language.toLowerCase() === "en"
      );
      if (defaultFound.length > 0) {
        return defaultFound[0].species;
      }
    } catch (error) {
      console.warn("Name and abbreviation not recognised", { error, species });
    }
    return out;
  };

  const getSpeciesNative = (species: string, countryCode: string) => {
    let out = { natural: false, fsc: false, pefc: false };
    if (!species || species === "" || !countryCode || countryCode === "") return out;
    try {
      // Get species
      const spc = speciesListRef.current![species];
      if (!spc || !spc.native) return out;
      // Get country
      const cc = spc.native[countryCode];
      if (!cc) return out;
      // Get keys from native object
      const keys = Object.keys(cc);
      for (const key of keys) {
        if (key === "natural") out.natural = cc[key] === "yes" ? true : false;
        // TODO: Insert FSC and PEFC specific logic here
        // For now just check if it is true or false
        if (key === "FSC") out.fsc = cc[key] ? true : false;
        if (key === "PEFC") out.pefc = cc[key] ? true : false;
      }
    } catch (error) {
      console.warn("Species native data not found", { error, species, countryCode });
    }
    return out;
  };

  interface GetSpeciesNameListProps {
    type?: "foliage" | "needle" | "forest-other";
    availability?: "simple" | "extended" | "full";
    filter?: "inventory";
    currentSpecies?: string;
  }
  const getSpeciesNameList = ({
    type,
    availability,
    filter,
    currentSpecies,
  }: GetSpeciesNameListProps) => {
    let list: { id: string; name: string }[] = [];
    try {
      Object.keys(speciesListRef.current!).forEach((spc) => {
        const spcType = getSpeciesType(spc);
        const spcAvailability = getSpeciesAvailability(spc);
        const nameAbr = getSpeciesNameAndAbbreviation(spc);
        let add = true;
        if (availability) {
          if (availability === "simple" && spcAvailability !== "simple") add = false;
          if (availability === "extended" && spcAvailability === "removed") add = false;
          if (currentSpecies && currentSpecies !== "" && currentSpecies === spc) add = true;
        }
        if (type) {
          if (type === "foliage" && spcType !== "foliage") add = false;
          if (type === "needle" && spcType !== "needle") add = false;
          if (type === "forest-other" && spcType !== "forest-other") add = false;
        }
        if (filter && filter === "inventory") {
          if (!INVENTORY_WOOD_LOG_SPECIES.includes(spc)) add = false;
        }
        // Add to list if true
        if (add) list.push({ id: spc, name: nameAbr.name });
      });
    } catch (error) {
      console.warn("Couldn't get name list", { error, species });
    }
    return list;
  };

  interface GetSpeciesOptionsProps {
    availability?: "simple" | "extended" | "full";
    currentSpecies?: string;
    filter?: "inventory";
    withoutHeaders?: boolean;
  }
  const getSpeciesOptions = ({
    availability,
    currentSpecies,
    filter,
    withoutHeaders,
  }: GetSpeciesOptionsProps) => {
    // Get foliage list
    const foliage = getSpeciesNameList({
      type: "foliage",
      availability,
      currentSpecies,
      filter,
    })
      .map((el) => {
        return {
          id: el.id,
          status: "enabled",
          label: el.name,
          color: getSpeciesColor(el.id),
        };
      })
      .sort((a, b) => {
        const valueA = typeof a.label === "string" ? a.label : "";
        const valueB = typeof b.label === "string" ? b.label : "";
        return valueA.localeCompare(valueB, "da-DK");
      });
    // Get needle list
    const needle = getSpeciesNameList({
      type: "needle",
      availability,
      currentSpecies,
      filter,
    })
      .map((el) => {
        return {
          id: el.id,
          status: "enabled",
          label: el.name,
          color: getSpeciesColor(el.id),
        };
      })
      .sort((a, b) => {
        const valueA = typeof a.label === "string" ? a.label : "";
        const valueB = typeof b.label === "string" ? b.label : "";
        return valueA.localeCompare(valueB, "da-DK");
      });
    // Get forest other list
    const forestOther = getSpeciesNameList({
      type: "forest-other",
      availability,
      currentSpecies,
      filter,
    })
      .map((el) => {
        return {
          id: el.id,
          status: "enabled",
          label: el.name,
          color: getSpeciesColor(el.id),
        };
      })
      .sort((a, b) => {
        const valueA = typeof a.label === "string" ? a.label : "";
        const valueB = typeof b.label === "string" ? b.label : "";
        return valueA.localeCompare(valueB, "da-DK");
      });
    let options: { id: string; status: string; label: string; color?: string; type?: string }[] =
      [];

    // If without headers, just return combined list
    if (withoutHeaders) return [...foliage, ...needle, ...forestOther];
    if (foliage.length > 0) {
      options = [
        {
          id: "header-foliage",
          status: "disabled",
          label: translate("header-species-foliage"),
          type: "text",
        },
        ...foliage,
      ];
    }
    if (needle.length > 0) {
      options = [
        ...options,
        {
          id: "header-needle",
          status: "disabled",
          label: translate("header-species-needle"),
          type: "text",
        },
        ...needle,
      ];
    }
    if (forestOther.length > 0) {
      options = [
        ...options,
        {
          id: "header-forest-other",
          status: "disabled",
          label: translate("header-species-other"),
          type: "text",
        },
        ...forestOther,
      ];
    }
    if (!filter || filter !== "inventory") {
      options.unshift({
        id: "non",
        status: "enabled",
        label: translate("non"),
        color: getSpeciesColor(""),
      });
    }
    return options;
  };

  // Nature areas
  const getAreaOptions = (type: "other" | "nature", currentSpecies?: string) => {
    const options: { id: string; label: string }[] = [];
    const curSpc = currentSpecies ? currentSpecies : "";
    try {
      Object.keys(speciesListRef.current!).forEach((spc) => {
        if (
          speciesListRef.current![spc].type === type &&
          (speciesListRef.current![spc].availability !== "removed" ||
            speciesListRef.current![spc].id === curSpc)
        ) {
          const nameAbr = getSpeciesNameAndAbbreviation(spc);
          options.push({ id: spc, label: nameAbr.name });
        }
      });
    } catch (error) {
      console.warn("Couldn't create area options", { error });
    }
    if (type === "nature")
      return options.sort((a, b) => sortAccordingToOrder(a.id, b.id, NATURE_CUSTOM_ORDER));
    return options.sort((a, b) => {
      const valueA = typeof a.label === "string" ? a.label : "";
      const valueB = typeof b.label === "string" ? b.label : "";
      return valueA.localeCompare(valueB);
    });
  };

  // --- Lines --- //
  const getLineStyle = (name?: string, styleObject?: FeatureStyleObject) => {
    // Set default style
    let style: { color: string; dashArray: string | null; weight: number } = {
      color: getColorComponentById(getCustomLineColorId(styleObject)),
      dashArray: null,
      weight: 1,
    };
    try {
      // Find color
      if (name && name !== "") {
        style.color = getSpeciesColor(name);
      }
      // Find dash array
      if (name && name !== "") {
        style.dashArray = speciesListRef.current![name].dashStyle
          ? (speciesListRef.current![name].dashStyle as string)
          : null;
      } else if (styleObject?.lineStyle) {
        if (styleObject?.lineStyle === "Solid") {
          style.dashArray = null;
        } else if (styleObject?.lineStyle === "Dashed") {
          style.dashArray = "20.6";
        }
      }
      // Find weight
      if (styleObject?.lineWidth) {
        style.weight = styleObject.lineWidth as number;
      }
    } catch (error) {
      console.warn("Couldn't find line style returning default", { error, name, styleObject });
    }
    return style;
  };

  const getLineOptions = () => {
    const options: { id: string; label: string }[] = [];
    try {
      Object.keys(speciesListRef.current!).forEach((spc) => {
        if (
          speciesListRef.current![spc].type === "line" &&
          speciesListRef.current![spc].availability !== "removed"
        ) {
          const nameAbr = getSpeciesNameAndAbbreviation(spc);
          options.push({ id: spc, label: nameAbr.name });
        }
      });
    } catch (error) {
      console.warn("Couldn't create line options", { error });
    }
    return options.sort((a, b) => sortAccordingToOrder(a.id, b.id, LINE_CUSTOM_ORDER));
  };

  const getPointOptions = () => {
    const options: PointOptionsProgramDefined = [];
    try {
      Object.keys(speciesListRef.current!).forEach((spc) => {
        if (
          speciesListRef.current![spc].type === "point" &&
          speciesListRef.current![spc].availability !== "removed"
        ) {
          const nameAbr = getSpeciesNameAndAbbreviation(spc);
          options.push({ uid: spc, id: getPointIconStrFromId(spc), label: nameAbr.name });
        }
      });
    } catch (error) {
      console.warn("Couldn't create point options", { error });
    }
    return options.sort((a, b) => sortAccordingToOrder(a.uid, b.uid, POINT_CUSTOM_ORDER));
  };

  const getPointIconStrFromId = (id: string) => {
    let iconStr = "FmdGoodRoundedIcon";
    try {
      if (speciesListRef.current![id] && speciesListRef.current![id].pointIcon) {
        iconStr = speciesListRef.current![id].pointIcon as string;
      }
    } catch (error) {
      console.warn("Couldn't get point icon", { error, id });
    }
    return iconStr;
  };

  const getPointInventoryIdGroupFromId = (id: string): InventoryIdGroup | null => {
    let group = null;
    try {
      if (speciesListRef.current![id] && speciesListRef.current![id].inventoryIdGroup) {
        group = speciesListRef.current![id].inventoryIdGroup as InventoryIdGroup;
      }
    } catch (error) {
      console.warn("Couldn't get point inventory id group", { error, id });
    }
    return group;
  };

  const getPointGroupFromId = (id: string) => {
    let group = "unknown";
    try {
      if (speciesListRef.current![id] && speciesListRef.current![id].pointGroup) {
        group = speciesListRef.current![id].pointGroup as string;
      }
    } catch (error) {
      console.warn("Couldn't get point group", { error, id });
    }
    return group;
  };

  const getPointInventoryWoodLogSpeciesOptions = (id: string) => {
    let speciesList: string[] = [];
  };

  const simulateSpecies = ({
    stand,
    horizonYears,
    autoThinningType,
    thinningDataPlanned,
    thinningStartAge,
    thinningInterval,
    thinningPercentage,
  }: SimulateSpeciesProps) => {
    return simulateSpeciesLib({
      stand,
      horizonYears,
      autoThinningType,
      thinningDataPlanned,
      thinningStartAge,
      thinningInterval,
      thinningPercentage,
      speciesList: speciesListRef.current,
    });
  };

  // --- Return --- //
  return {
    species: speciesListRef.current,
    schemeChanged,
    getSpeciesColor,
    getSpeciesModelType,
    getSpeciesType,
    getSpeciesNameAndAbbreviation,
    getSpeciesNative,
    getSpeciesNameList,
    getSpeciesOptions,
    getAreaOptions,
    getLineStyle,
    getLineOptions,
    getPointOptions,
    getPointIconStrFromId,
    getPointGroupFromId,
    getPointInventoryIdGroupFromId,
    simulateSpecies,
  };
};

export default useSpeciesData;
